import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Star, Mail, Phone, Briefcase, Award, 
  MessageSquare, Calendar, User, MapPin, GraduationCap,
  TrendingUp, CheckCircle, Clock, Loader2, Eye, X
} from 'lucide-react';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import mentorService from '@services/mentor.service';
import mentorFeedbackService from '@services/mentorFeedback.service';
import profileExtendedService from '@services/profileExtended.service';
import apiService from '@services/api/api.service';
import toast from 'react-hot-toast';
import { ROUTES } from '@constants';

const MentorProfileDetails = () => {
  const { mentorId } = useParams();
  const navigate = useNavigate();
  const [mentor, setMentor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [skills, setSkills] = useState([]);
  const [profileData, setProfileData] = useState(null);
  const [stats, setStats] = useState({
    questionsAnswered: 0,
    averageResponseTime: 'N/A',
    successRate: 0,
    totalStudents: 0
  });
  const [viewingExpertise, setViewingExpertise] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);

  useEffect(() => {
    fetchMentorDetails();
    fetchFeedbacks();
  }, [mentorId]);

  const fetchMentorDetails = async () => {
    try {
      setLoading(true);
      const response = await mentorService.getMentorById(mentorId);
      
      if (response.success) {
        const mentorData = response.data?.data || response.data;
        setMentor(mentorData);
      }

      try {
        const statsResponse = await apiService.get(`/profile/mentor/${mentorId}/stats`);
        if (statsResponse.success) {
          const statsData = statsResponse.data?.data || statsResponse.data;
          setStats({
            questionsAnswered: statsData.questionsAnswered || 0,
            averageResponseTime: statsData.averageResponseTime || 'N/A',
            successRate: statsData.successRate || 0,
            totalStudents: statsData.studentsHelped || 0
          });
        }
      } catch (err) {
        console.error('Failed to fetch mentor stats:', err);
      }

      try {
        const profileResponse = await apiService.get(`/profile/${mentorId}`);
        if (profileResponse.success) {
          const fullProfile = profileResponse?.data?.data || profileResponse?.data;
          setProfileData(fullProfile);
        }
      } catch (err) {
        console.error('Failed to fetch full profile:', err);
      }

      try {
        const skillsResponse = await profileExtendedService.getSkillsByUserId(mentorId);
        if (skillsResponse.success) {
          const skillsData = skillsResponse.data?.data || skillsResponse.data || [];
          setSkills(Array.isArray(skillsData) ? skillsData : []);
        }
      } catch (err) {
        console.error('Failed to fetch skills:', err);
      }
    } catch (error) {
      console.error('Failed to fetch mentor details:', error);
      toast.error('Failed to load mentor details');
    } finally {
      setLoading(false);
    }
  };

  const fetchFeedbacks = async () => {
    try {
      const response = await mentorFeedbackService.getMentorFeedbacks(mentorId, 10, 0);
      if (response.success) {
        setFeedbacks(response.data?.data?.feedbacks || []);
      }
    } catch (error) {
      console.error('Failed to fetch feedbacks:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 text-secondary-500 animate-spin" />
      </div>
    );
  }

  if (!mentor) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <User className="w-16 h-16 text-gray-300 mb-4" />
        <h3 className="text-lg font-bold text-primary-700 mb-2">Mentor Not Found</h3>
        <Button onClick={() => navigate(ROUTES.MENTOR_MENTORS_LIST)} variant="outline">
          Back to Mentors
        </Button>
      </div>
    );
  }

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${import.meta.env.VITE_API_BASE_URL.replace('/api', '')}${path}`;
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <Button
        variant="outline"
        onClick={() => navigate(ROUTES.MENTOR_MENTORS_LIST)}
        className="mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Mentors
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-6">
          <Card className="p-6">
            <div className="flex flex-col items-center text-center">
              {mentor.profilePicture ? (
                <img
                  src={getImageUrl(mentor.profilePicture)}
                  alt={`${mentor.firstName} ${mentor.lastName}`}
                  className="w-32 h-32 rounded-full object-cover mb-4 border-4 border-secondary-200"
                />
              ) : (
                <div className="w-32 h-32 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white text-4xl font-bold mb-4">
                  {mentor.firstName?.[0]}{mentor.lastName?.[0]}
                </div>
              )}
              
              <h1 className="text-2xl font-bold text-primary-700 mb-2">
                {mentor.firstName} {mentor.lastName}
              </h1>
              
              {mentor.currentPosition && (
                <p className="text-secondary-600 font-semibold mb-3">
                  {mentor.currentPosition}
                </p>
              )}

              <div className="flex items-center gap-2 mb-4">
                <div className="flex items-center gap-1 bg-yellow-50 px-3 py-1.5 rounded-full border border-yellow-200">
                  <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  <span className="font-bold text-primary-700">
                    {mentor.averageRating || 'N/A'}
                  </span>
                  {mentor.totalRatings > 0 && (
                    <span className="text-sm text-gray-600">({mentor.totalRatings})</span>
                  )}
                </div>
              </div>

              {mentor.bio && (
                <p className="text-gray-600 text-sm mb-4">
                  {mentor.bio}
                </p>
              )}
            </div>

            <div className="border-t border-gray-200 pt-4 mt-4 space-y-3">
              {mentor.email && (
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="w-4 h-4 text-primary-500 flex-shrink-0" />
                  <span className="text-gray-700 break-all">{mentor.email}</span>
                </div>
              )}
              
              {mentor.phone && (
                <div className="flex items-center gap-3 text-sm">
                  <Phone className="w-4 h-4 text-primary-500 flex-shrink-0" />
                  <span className="text-gray-700">{mentor.phone}</span>
                </div>
              )}
              
              {mentor.yearsOfExperience && (
                <div className="flex items-center gap-3 text-sm">
                  <Briefcase className="w-4 h-4 text-primary-500 flex-shrink-0" />
                  <span className="text-gray-700">{mentor.yearsOfExperience} years experience</span>
                </div>
              )}
              
              {profileData?.city && (
                <div className="flex items-center gap-3 text-sm">
                  <MapPin className="w-4 h-4 text-primary-500 flex-shrink-0" />
                  <span className="text-gray-700">{profileData.city}</span>
                </div>
              )}
              
              {profileData?.highestQualification && (
                <div className="flex items-center gap-3 text-sm">
                  <GraduationCap className="w-4 h-4 text-primary-500 flex-shrink-0" />
                  <span className="text-gray-700">{profileData.highestQualification}</span>
                </div>
              )}
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-bold text-primary-700 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Performance Stats
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Questions Answered</span>
                <span className="font-bold text-primary-700">{stats.questionsAnswered}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Students Helped</span>
                <span className="font-bold text-primary-700">{stats.totalStudents}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Avg Response Time</span>
                <span className="font-bold text-primary-700">{stats.averageResponseTime}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Success Rate</span>
                <span className="font-bold text-primary-700">{stats.successRate}%</span>
              </div>
            </div>
          </Card>
        </div>

        <div className="lg:col-span-2 space-y-6">
          {mentor.areasOfExpertise && mentor.areasOfExpertise.length > 0 && (
            <Card className="p-6">
              <h3 className="text-lg font-bold text-primary-700 mb-4 flex items-center gap-2">
                <Award className="w-5 h-5" />
                Areas of Expertise
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {mentor.areasOfExpertise.map((expertise, index) => (
                  <div
                    key={index}
                    onClick={() => setViewingExpertise(expertise)}
                    className="p-4 bg-gradient-to-br from-primary-50 to-secondary-50 rounded-lg border border-primary-200 hover:border-secondary-400 transition-all cursor-pointer group"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-primary-700 mb-1 group-hover:text-secondary-600 transition-colors">
                          {expertise.title}
                        </h4>
                        <p className="text-xs text-gray-600 line-clamp-2">
                          {expertise.description}
                        </p>
                      </div>
                      <Eye className="w-4 h-4 text-primary-400 group-hover:text-secondary-500 transition-colors flex-shrink-0 ml-2" />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {skills.length > 0 && (
            <Card className="p-6">
              <h3 className="text-lg font-bold text-primary-700 mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Skills
              </h3>
              <div className="flex flex-wrap gap-2">
                {skills.map((skill, index) => (
                  <span
                    key={index}
                    className="px-3 py-1.5 bg-primary-100 text-primary-700 rounded-full text-sm font-medium border border-primary-200"
                  >
                    {skill.skillName}
                  </span>
                ))}
              </div>
            </Card>
          )}

          {feedbacks.length > 0 && (
            <Card className="p-6">
              <h3 className="text-lg font-bold text-primary-700 mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Student Feedback ({feedbacks.length})
              </h3>
              <div className="space-y-4">
                {feedbacks.map((feedback) => (
                  <div key={feedback.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                          {feedback.studentName?.[0] || 'S'}
                        </div>
                        <div>
                          <p className="font-semibold text-primary-700 text-sm">
                            {feedback.studentName || 'Student'}
                          </p>
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-3 h-3 ${
                                  i < feedback.rating
                                    ? 'text-yellow-500 fill-yellow-500'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(feedback.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">{feedback.comment}</p>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      {viewingExpertise && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-xl font-bold text-primary-700">{viewingExpertise.title}</h3>
              <button
                onClick={() => setViewingExpertise(null)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <p className="text-gray-700 whitespace-pre-wrap">{viewingExpertise.description}</p>
          </Card>
        </div>
      )}
    </div>
  );
};

export default MentorProfileDetails;
